// §4.2 — THE ASSIGNMENTS PANEL (Module 09 §2.2, D-241's flagged §5 omission).
//
// `customer_assignments` has carried `role='collaborator'` since P3-A and §3.4
// grants collaborators the right to send a customer's quotes — with no route
// to create one. The CRM benchmark flagged it; the two endpoints landed with
// this slice; this is their surface.
//
// THE LAST-OWNER GUARD IS DISCLOSED BEFORE THE PRESS, not discovered from a
// 409. `LAST_OWNER_ASSIGNMENT` is a real contract refusal and the server is
// still the boundary — but a customer with no owner has no `owner_user_id` for
// §4.5's filter, nobody for §3.6's follow-up task and nobody the §4.6 card
// names as requester, so the guard is a rule about the business rather than a
// technicality. A reader deserves to know it before they try, which is why the
// only owner's Remove is disabled with the reason beside it rather than live
// and refusing.
//
// SO IS THE OWNER SWAP. `customer_assignments` admits ONE owner, and the
// contract makes adding a second RE-POINT ownership — the prior owner becomes
// a collaborator in the same transaction, because a refusal would make
// hand-over a two-step dance with a window where nobody owns the account. That
// is a surprising thing for a form to do silently, so the form says it above
// the button.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { HelpTip } from "../../../../components/help-tip";
import { Field } from "../../../../components/field";
import { RefLabel } from "../../../../components/ref-label";
import { Select } from "../../../../components/select";
import { StatusChip } from "../../../../components/status-chip";
import { MutationFailureNote } from "../mutation-failure-note";
import { useOwnerIndex } from "../hooks/sales-queries";
import { useMemberRefAsk, useReadsHeldOffline } from "../lib/reads";
import { useAddAssignment, useRemoveAssignment } from "../hooks/quote-queries";

interface Assignment {
  readonly user_id: string;
  readonly role: string;
}

export function AssignmentsPanel({
  customerId,
  assignments,
  canManage,
  isAdmin,
}: {
  customerId: string;
  assignments: readonly Assignment[];
  /** `sales.manage_customers` — the code both routes ride. */
  canManage: boolean;
  /** ga/fa: may read the member directory the picker is built from. */
  isAdmin: boolean;
}) {
  const { t } = useTranslation();
  const members = useOwnerIndex(isAdmin);
  // A directory read the browser holds offline reports pending until the
  // network returns; the rows then read as short ids, not a resolving mark.
  const offline = useReadsHeldOffline();
  const memberRef = useMemberRefAsk(members.isPending && !offline);
  const add = useAddAssignment(customerId);
  const remove = useRemoveAssignment(customerId);

  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<"owner" | "collaborator">("collaborator");

  const owners = assignments.filter((a) => a.role === "owner");
  const assigned = new Set(assignments.map((a) => a.user_id));
  // The picker offers only people who are NOT already assigned — re-adding an
  // existing assignment is `ASSIGNMENT_EXISTS`, and an option whose every
  // submission is refused is a control that exists to fail.
  const candidates = [...members.byId.entries()].filter(([id]) => !assigned.has(id));

  return (
    <Card
      className="sales-360-card sales-assignments"
      data-testid="sales-360-assignments"
      title={t("sales.c360.assignments.title")}
      action={
        <HelpTip
          label={t("nav.helpAbout", { topic: t("sales.c360.assignments.title") })}
          data-testid="sales-360-assignments-help"
        >
          {t("sales.c360.assignments.body")}
        </HelpTip>
      }
    >
      {assignments.length === 0 ? (
        <p className="sales-hint" data-testid="sales-assignments-empty">
          {t("sales.c360.assignments.empty")}
        </p>
      ) : (
        <ul className="sales-assignment-list">
          {assignments.map((assignment) => {
            const isOnlyOwner = assignment.role === "owner" && owners.length === 1;
            return (
              <li
                key={assignment.user_id}
                data-testid="sales-assignment-row"
                data-role={assignment.role}
              >
                <RefLabel
                  value={members.byId.get(assignment.user_id)}
                  id={assignment.user_id}
                  pending={members.isPending && !offline}
                  {...memberRef}
                />
                <StatusChip
                  variant={assignment.role === "owner" ? "accent" : "neutral"}
                  dot={false}
                >
                  {assignment.role === "owner"
                    ? t("sales.c360.assignments.roleOwner")
                    : t("sales.c360.assignments.roleCollaborator")}
                </StatusChip>
                {canManage ? (
                  <>
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={isOnlyOwner}
                      busy={remove.isPending}
                      busyLabel={t("sales.c360.assignments.removing")}
                      data-testid="sales-assignment-remove"
                      onClick={() => {
                        add.reset();
                        remove.mutate(assignment.user_id);
                      }}
                    >
                      {t("sales.c360.assignments.remove")}
                    </Button>
                    {isOnlyOwner ? (
                      // The guard, stated where the disabled control is.
                      <span className="sales-hint" data-testid="sales-assignment-last-owner">
                        {t("sales.c360.assignments.lastOwnerGuard")}
                      </span>
                    ) : null}
                  </>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {add.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.c360.assignments.addFailed")}
          error={add.error}
        />
      ) : null}
      {remove.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.c360.assignments.removeFailed")}
          error={remove.error}
        />
      ) : null}

      {canManage ? (
        <div className="filter-bar" data-testid="sales-assignment-add">
          <Field label={t("sales.c360.assignments.member")}>
            <Select
              value={userId}
              data-testid="sales-assignment-user"
              onChange={(e) => {
                setUserId(e.target.value);
              }}
            >
              <option value="">{t("sales.c360.assignments.add")}</option>
              {candidates.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("sales.c360.assignments.role")}>
            <Select
              value={role}
              data-testid="sales-assignment-role"
              onChange={(e) => {
                setRole(e.target.value === "owner" ? "owner" : "collaborator");
              }}
            >
              <option value="collaborator">{t("sales.c360.assignments.roleCollaborator")}</option>
              <option value="owner">{t("sales.c360.assignments.roleOwner")}</option>
            </Select>
          </Field>
          {role === "owner" && owners.length > 0 ? (
            <p className="sales-hint" data-testid="sales-assignment-owner-swap">
              {t("sales.c360.assignments.ownerSwap")}
            </p>
          ) : null}
          <Button
            variant="primary"
            size="sm"
            disabled={userId.length === 0}
            busy={add.isPending}
            busyLabel={t("sales.common.saving")}
            data-testid="sales-assignment-add-confirm"
            onClick={() => {
              remove.reset();
              add.mutate(
                { user_id: userId, role },
                {
                  onSuccess: () => {
                    setUserId("");
                    setRole("collaborator");
                  },
                },
              );
            }}
          >
            {t("sales.c360.assignments.addTitle")}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}
